import React, { useMemo, useState } from "react";

const formatearJson = (valor) => {
  if (valor === undefined) {
    return "—";
  }
  try {
    return JSON.stringify(valor, null, 2);
  } catch (_) {
    return String(valor);
  }
};

const formatearMonto = (monto) => {
  const n = Number(monto);
  if (!Number.isFinite(n)) {
    return "—";
  }
  return `S/ ${n.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const TablaLineasJatsa = ({ lineas, mostrarAplica = false }) => {
  if (!lineas?.length) {
    return <p className="text-sm text-slate-600">No hay líneas para mostrar.</p>;
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-slate-700">
          <tr>
            <th className="text-left font-semibold px-3 py-2">Empleado</th>
            <th className="text-left font-semibold px-3 py-2">DNI</th>
            <th className="text-left font-semibold px-3 py-2">Código</th>
            <th className="text-left font-semibold px-3 py-2">Concepto</th>
            <th className="text-right font-semibold px-3 py-2">Monto</th>
            {mostrarAplica ? (
              <th className="text-center font-semibold px-3 py-2">Aplica</th>
            ) : null}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {lineas.map((fila, index) => (
            <tr
              key={`${fila.empleado_id}-${fila.codigo_plame}-${index}`}
              className={fila.encontrado_en_planillas === false ? "bg-amber-50" : "bg-white"}
            >
              <td className="px-3 py-2 text-slate-900">
                <div className="font-medium">{fila.nombre_completo}</div>
                {fila.cargo ? <div className="text-xs text-slate-500">{fila.cargo}</div> : null}
              </td>
              <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{fila.numero_documento || "—"}</td>
              <td className="px-3 py-2 font-mono text-slate-800">{fila.codigo_plame}</td>
              <td className="px-3 py-2 text-slate-700">{fila.descripcion}</td>
              <td className="px-3 py-2 text-right font-medium text-slate-900 whitespace-nowrap">
                {formatearMonto(fila.monto)}
              </td>
              {mostrarAplica ? (
                <td className="px-3 py-2 text-center">
                  {fila.aplica_en_planillas ? (
                    <span className="text-teal-700 font-medium">Sí</span>
                  ) : (
                    <span className="text-slate-400">No</span>
                  )}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const JatsaConceptosApiModal = ({ isOpen, onClose, inspeccion, cargando = false }) => {
  const [pestaña, setPestaña] = useState("empleados");

  const vista = inspeccion?.vista_empleados;

  const textoJson = useMemo(() => {
    if (!inspeccion) {
      return "";
    }
    if (pestaña === "completo") {
      return formatearJson(inspeccion);
    }
    if (pestaña === "paquete_plame") {
      return formatearJson(inspeccion.paquete_plame);
    }
    if (pestaña === "jatsa_api") {
      return formatearJson(inspeccion.jatsa_api);
    }
    return "";
  }, [inspeccion, pestaña]);

  const copiar = async () => {
    const texto =
      pestaña === "empleados" || pestaña === "api_tabla"
        ? formatearJson(vista)
        : textoJson;
    if (!texto || texto === "—") {
      return;
    }
    try {
      await navigator.clipboard.writeText(texto);
    } catch (error) {
      console.error("No se pudo copiar", error);
    }
  };

  if (!isOpen) {
    return null;
  }

  const disponible = inspeccion?.disponible;
  const sinPublicar = disponible === false;
  const esJson = ["jatsa_api", "paquete_plame", "completo"].includes(pestaña);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Conceptos PLAME desde Jatsa</h2>
            <p className="text-sm text-slate-600 mt-1">
              Montos del satélite enlazados al nombre del trabajador en planillas.
            </p>
            {vista?.periodo ? (
              <p className="text-xs text-slate-500 mt-1">
                Periodo {vista.periodo}
                {vista.enviado_en ? ` · publicado ${vista.enviado_en}` : ""}
              </p>
            ) : null}
            {inspeccion?.consultadoEn ? (
              <p className="text-xs text-slate-500">Consultado: {inspeccion.consultadoEn}</p>
            ) : null}
            {sinPublicar ? (
              <p className="text-sm text-amber-700 mt-2">
                Jatsa aún no publicó conceptos (HTTP {inspeccion?.jatsa_http_status ?? 404}).
              </p>
            ) : null}
            {vista?.resumen ? (
              <p className="text-sm text-slate-700 mt-2">
                {vista.resumen.trabajadores_con_conceptos} trabajador(es) · {vista.resumen.lineas_que_aplican}{" "}
                línea(s) que se aplican en PLAME
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-800 text-xl leading-none px-2"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <div className="px-6 pt-3 flex flex-wrap gap-2 border-b border-slate-100">
          {[
            { id: "empleados", label: "Por empleado (aplica)" },
            { id: "api_tabla", label: "Todo lo que envió Jatsa" },
            { id: "jatsa_api", label: "JSON crudo" },
            { id: "paquete_plame", label: "JSON interpretado" },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setPestaña(tab.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${
                pestaña === tab.id
                  ? "bg-indigo-700 text-white border-indigo-700"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-auto p-6">
          {cargando ? (
            <p className="text-slate-600 text-sm">Consultando Jatsa…</p>
          ) : pestaña === "empleados" ? (
            <TablaLineasJatsa lineas={vista?.lineas_que_aplican} />
          ) : pestaña === "api_tabla" ? (
            <>
              <p className="text-xs text-slate-600 mb-3">
                Incluye conceptos que Jatsa envió aunque planillas no los use (código distinto de 0909/0917/0902 o monto
                cero).
              </p>
              <TablaLineasJatsa lineas={vista?.lineas_api} mostrarAplica />
            </>
          ) : (
            <pre className="text-xs leading-relaxed bg-slate-950 text-slate-100 rounded-xl p-4 overflow-x-auto whitespace-pre-wrap break-words">
              {textoJson || "Sin datos. Usa «Ver datos de la API» o «Consultar Jatsa» primero."}
            </pre>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={copiar}
            disabled={cargando || (esJson && (!textoJson || textoJson === "—"))}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Copiar
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-slate-800 text-white hover:bg-slate-900"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default JatsaConceptosApiModal;
